import { autoinject, transient } from "aurelia-dependency-injection";
import { bindable, BindingEngine, Disposable, computedFrom, observable } from "aurelia-framework";
import { TransformHtmlHelper } from "../helpers/transform-html-helper";
import { PropertyMetaModel } from "../../../../models/basics/basics.models";
import { DataType, QueryFilter } from "../../../../models/queries/queries.models";
import { FormMetaConfig, FormMetaPage, ConditionalGroup, FormMetaItem, InfoSettingsHelper } from "../../../components/editors/form-editor/form-meta-config";
import * as Enumerable from "linq";
import { OnboardingSession } from "../onboarding.session";
import { BaseOnboardingSection } from "../sections/base-onboarding-section";
import { OnboardingRequestRuleModel } from "../../../../models/onboarding/onboarding.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { UrlHelper } from "../../../../helpers/url-helper";
import { Reflection } from "../../../../utilities/reflection";
import * as moment from "moment";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject()
@transient()
export class ClientMetaComponent {

    private _subscriptions: Disposable[] = [];

    sections: { [guid: string]: { currentViewModel: BaseOnboardingSection } } = {};
    allOnePage: boolean = false;
    ifBindables: { [guid: string]: { isVisible: boolean, dependencies: string[], properties: string[], conditionalGroup: ConditionalGroup} } = {};
    pages: ClientMetaPageModel[] = [];

    @observable()
    currentPageNumber: number = 0;

    @computedFrom(
        Reflection.getPropertyPath<ClientMetaComponent>(x => x.allOnePage),
        Reflection.getPropertyPath<ClientMetaComponent>(x => x.currentPageNumber)
    )
    get canNext(): boolean {
        return !this.allOnePage && this.currentPageNumber < this.pages.length - 1;
    }

    @computedFrom(
        Reflection.getPropertyPath<ClientMetaComponent>(x => x.allOnePage),
        Reflection.getPropertyPath<ClientMetaComponent>(x => x.currentPageNumber)
    )
    get canPrevious(): boolean {
        return !this.allOnePage && this.currentPageNumber > 0;
    }

    @observable()
    currentPage: ClientMetaPageModel;

    @bindable()
    configurations: ClientMetaModel[];

    @bindable()
    readonly: boolean = false;

    @bindable()
    forceValidations: boolean = false;

    @bindable()
    transformedHtmlCallback: (item: FormMetaItem, element: HTMLElement, property: string, dataType: DataType) => void = null;

    constructor(private bindingEngine: BindingEngine, private onboardingSession: OnboardingSession, private dialogClientService: DialogClientService, 
        protected configService: ConfigService) { }

    public propertyChanged() {
        this.bind();
    }

    public transformedHtml(configuration: ClientMetaModel) {
        const htmlTransformed =
            TransformHtmlHelper.transformHtml(
                configuration.config,
                this.readonly,
                configuration.model,
                configuration.properties,
                (element: HTMLElement, property: string, dataType: DataType) => this.transformedHtmlCallback ? this.transformedHtmlCallback(null, element, property, dataType) : {}, this.forceValidations);
        return htmlTransformed;
    }

    public areConditionsSatisfied(item: any, filters: QueryFilter[]): boolean {
        const groupedFilters = Enumerable.from(filters).groupBy(x => x.groupGuid);
        let response: boolean = true;
        groupedFilters.forEach(filterGroup => {
            let groupResponse: boolean = true;
            filterGroup.forEach(filter => {
                const operator = filter.filterOperator.friendlyName;
                const type = filter.filterProperty.type;
                if (filter.isAnd) {
                    groupResponse = groupResponse && this.isEqual(item[filter.filterProperty.name], operator, type, filter.filterValue, filter.filterProperty.isCollection);
                }
                else {
                    groupResponse = groupResponse || this.isEqual(item[filter.filterProperty.name], operator, type, filter.filterValue, filter.filterProperty.isCollection);
                }
            });
            response = response && groupResponse;
        });
        return response;
    }

    attached() {
        window.addEventListener('popstate', this.onPopState, false);
    }


    onPopState = () => {
        const currentPage = UrlHelper.getParameterValueByKey('page');

        if (currentPage != this.currentPageNumber.toString()) {
            this.currentPageNumber = currentPage ? Number(currentPage) : 0;
            this.currentPage = this.pages[this.currentPageNumber];
        }
    }

    public bind() {
        let allOnePage = true;
        const pages: ClientMetaPageModel[] = [];
        this.configurations.forEach(configuration => {

            configuration.metaConfig = configuration.isMetaFormStyle ? JSON.parse(configuration.config) : configuration.config;
            if (configuration.isMetaFormStyle) {
                if (!(<FormMetaConfig>configuration.metaConfig).pages) {
                    (<FormMetaConfig>configuration.metaConfig).pages = [];
                }
                (<FormMetaConfig>configuration.metaConfig).pages.forEach((page, index) => {

                    page.items.forEach(item => {
                        const propsBinded: string[] = [];

                        const toolTip = item.infoSettings ? InfoSettingsHelper.migrateInfoSettings(item.infoSettings).infoTooltip : null;

                        const answerElement = TransformHtmlHelper.transformHtml(item.answerHtml, this.readonly, configuration.model, configuration.properties,
                            (element: HTMLElement, property: string, dataType: DataType) => {
                                propsBinded.push(property);
                                return this.transformedHtmlCallback ? this.transformedHtmlCallback(item, element, property, dataType) : {};
                            }, this.forceValidations, toolTip);
                        item.answer = answerElement;
                        item.isVisible = item.answer && (!!item.answer.textContent || item.answer.getElementsByClassName('transformed-element').length > 0);
                        item.rules = (configuration.rules || []).filter(z => propsBinded.indexOf(z.property) > -1).map(x => this.getMessage(x));

                        if (item.conditionalGroupGuid && page.conditionalGroups[item.conditionalGroupGuid] && !this.ifBindables[item.conditionalGroupGuid]) {
                            this.ifBindables[item.conditionalGroupGuid] = {
                                isVisible: true,
                                conditionalGroup: page.conditionalGroups[item.conditionalGroupGuid],
                                dependencies: page.conditionalGroups[item.conditionalGroupGuid].filters.map(x => x.filterProperty.name),
                                properties: propsBinded
                            }
                        }
                    });
                    pages.push({
                        clientMetaModel: configuration,
                        page: page,
                        content: null,
                        rulesNumber: Enumerable.from(page.items).sum(x => x.rules.length),
                        isVisible: (page.items.length > 0 && page.items.some(x => x.section != null || x.isVisible))
                            || !!page.title || !!page.subtitle
                    });
                    if (index > 0) {
                        allOnePage = false;
                    }
                });
            } else {
                const content = this.transformedHtml(configuration);
                pages.push({
                    clientMetaModel: configuration,
                    page: null,
                    content: content,
                    rulesNumber: 0,
                    isVisible: content && (!!content.textContent || content.getElementsByClassName('transformed-element').length > 0)
                });
            }

            const model = configuration.model;
            const keys = Object.keys(configuration.model);
            const ifKeys = Object.keys(this.ifBindables);
            keys.forEach(key => {
                if (model[key] === undefined) {
                    model[key] = null;
                }

                const ifBindablesOnKey = ifKeys.filter(ifKey => this.ifBindables[ifKey] && this.ifBindables[ifKey].dependencies.some(z => z == key));

                const onPropChanged = () => {

                    if (ifBindablesOnKey.length > 0) {
                        ifBindablesOnKey.forEach(ifKey => {
                            const isVisible = this.areConditionsSatisfied(model, this.ifBindables[ifKey].conditionalGroup.filters);
                            this.ifBindables[ifKey].isVisible = isVisible;
                        });

                        const properties = Enumerable.from(ifBindablesOnKey)
                            .where((ifKey => !this.ifBindables[ifKey].isVisible))
                            .selectMany((ifKey => this.ifBindables[ifKey].properties))
                            .distinct()
                            .toArray();

                        properties.forEach(prop => {
                            if (Object.keys(this.ifBindables)
                                .filter(ifKey => this.ifBindables[ifKey].properties.indexOf(prop) > -1)
                                .every(ifKey => !this.ifBindables[ifKey].isVisible)) {
                                model[prop] = null;
                            }
                        });
                    }
                }
                this._subscriptions.push(this.bindingEngine.propertyObserver(model, key).subscribe(() => {
                    onPropChanged();
                }));
                onPropChanged();
            });
        });
        this.pages = pages.filter(x => x.isVisible);
        this.allOnePage = allOnePage;
        this.currentPageNumber = 0;
        this.currentPage = this.pages[this.currentPageNumber];
    }

    getMessage(rule: OnboardingRequestRuleModel) {
        if (!rule.clientSideParameters || !rule.clientSideParameters.length) {
            return rule.message;
        }
        else {
            let i;
            for (i = 0; i < rule.clientSideParameters.length; i++) {

                const momentObj = moment.utc(rule.clientSideParameters[i]);

                if (momentObj.isValid()) {
                    return rule.message.replace("{" + i + "}", momentObj.local().format(this.configService.config.common.defaultDateFormat));
                }

                return rule.message.replace("{" + i + "}", rule.clientSideParameters[i]);
            }
        }
    }

    public currentPageNumberChanged() {
        if (window["onboardingRequestUrlMeta"]) {
            window["onboardingRequestUrlMeta"].currentMetaPageNumber = this.currentPageNumber;
        }
    }

    public detached() {
        if (this._subscriptions) {
            this._subscriptions.forEach(sub => sub.dispose());
            this._subscriptions = [];
        }

        this.sections = {}
        window.removeEventListener('popstate', this.onPopState, false);
    }

    lastPage(): void {
        setTimeout(() => {
            this.currentPageNumber = this.pages.length - 1;
            this.currentPage = this.pages[this.currentPageNumber];
        });
    }

    goToPage(index: number) {
        this.currentPageNumber = index;
        this.currentPage = this.pages[this.currentPageNumber];
    }

    nextPage(): boolean {
        if (this.canNext) {
            ++this.currentPageNumber;
            this.currentPage = this.pages[this.currentPageNumber];
            return true;
        } else {
            return false;
        }
    }

    previousPage(): boolean {
        if (this.canPrevious) {
            history.back();
            this.onboardingSession.popUrl();
            return true;
        } else {
            return false;
        }
    }

    public currentPageChanged() {
        const pageFromUrl = Number(UrlHelper.getParameterValueByKey('page') || '0');
        if (this.currentPageNumber > pageFromUrl) {
            const newUrl = UrlHelper.updateURL({
                'page': this.currentPageNumber
            }, []);
            const urlOnboardingRequestGuid = UrlHelper.getParameterValueByKey("onboardingRequestGuid", newUrl)
            if (urlOnboardingRequestGuid) {
                this.onboardingSession.pushUrl(newUrl);
            }
        }
    }

    isEqual = (valueToTest, symbol, type, value, isCollection) => {
        type = Number(type);
        if (type == DataType.Number || type == DataType.Double) {
            valueToTest = valueToTest != null ? Number(valueToTest) : null;
            value = value != null ? Number(value) : null;
        } else if (isCollection) {
            valueToTest = valueToTest ? valueToTest.map(x => x.toString()) : valueToTest;
            value = value ? value.toString() : value;
        }
        if (symbol == "=") {
            return this.getEquality(valueToTest, type, value);
        } else if (symbol == "<>") {
            return valueToTest != value;
        } else if (symbol == "<=") {
            return valueToTest && valueToTest <= value;
        } else if (symbol == ">=") {
            return valueToTest && valueToTest >= value;
        } else if (symbol == "<") {
            return valueToTest && valueToTest < value;
        } else if (symbol == ">") {
            return valueToTest && valueToTest > value;
        } else if (symbol == "ends with") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) == (valueToTest.length - 1);
        } else if (symbol == "starts with") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) == 0;
        } else if (symbol == "contains") {
            return valueToTest && valueToTest.length > 0 && valueToTest.indexOf(value) > -1;
        } else if (symbol == "is empty") {
            return !valueToTest;
        } else if (symbol == "is not empty") {
            return !!valueToTest;
        }
    }

    getEquality = (valueToTest, type, value) => {
        if (type == DataType.Boolean) {
            if ((valueToTest == null && value != null) ||
                (valueToTest != null && value == null)) {
                return false;
            }
            return !!valueToTest == !!value;
        }
        return valueToTest == value;
    }

    public sectionViewModel(item: FormMetaItem) {
        if (!item.section || !this.onboardingSession || !this.onboardingSession.onboardingRequestsPath || !this.onboardingSession.pluginName) {
            return;
        }

        const path = `${this.onboardingSession.onboardingRequestsPath}/${this.onboardingSession.pluginName}/sections/${item.section}/${item.section}`;

        return path;
    }

    public sectionModel(item: FormMetaItem) {
        return {
            readonly: this.readonly,
            guid: item.guid
        };
    }

    public saveSections() {
        const promises = Object.keys(this.sections).map(key => this.sections[key]).filter(s => s && s.currentViewModel).map(s => s.currentViewModel.save());

        return new Promise<any>((resolve, reject) => {
            Promise.all(promises).then(resolve).catch(reject);
        });
    }

    public viewFullImage(url: string) {
        this.dialogClientService.show(DialogServiceConstants.URLs.ImageViewer, {
            link: url,
            isDataUrl: true
        }, false, false, true);
    }

    public openInfoDialog(formMetaItem: FormMetaItem) {
        if (formMetaItem.infoSettings && InfoSettingsHelper.migrateInfoSettings(formMetaItem.infoSettings).infoDialog) {
            this.dialogClientService.show(DialogServiceConstants.URLs.QuestionInfoDialog, formMetaItem, false, true);
        }
    }
}

export interface ClientMetaPageModel {
    page: FormMetaPage;
    content: HTMLElement;
    clientMetaModel: ClientMetaModel;
    rulesNumber: number;
    isVisible: boolean;
}

export interface ClientMetaModel {
    isMetaFormStyle: boolean;
    config: string;
    properties: PropertyMetaModel[];
    model: any;
    metaConfig?: string | FormMetaConfig;
    fallbackTitle: string;
    fallbackSubtitle: string;
    doNotUseFallback: boolean;
    rules?: OnboardingRequestRuleModel[];
}
